import { Box, CircularProgress, Dialog, DialogContent, IconButton, Stack, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"
import { api } from '../../../api/api'
import { connect } from "react-redux"
import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { defaultCatch, defaultSuccess } from '../../../state/actions'
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material'

function IDPhotoDialog(props) {
  const { open, onClose, credential_photo, defaultSuccess, defaultCatch } = props
  const photoId = credential_photo?.id
  const [photo, setPhoto] = useState('')
  const [isFetchingPhoto, setIsFetchingPhoto] = useState(false)
  const [isImageLoading, setIsImageLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const photoUrlRef = useRef('')
  const { t } = useTranslation()
  const navigate = useNavigate()

  useEffect(() => {
    if (!open) return

    if (!photoId) {
      setPhoto('')
      setHasError(true)
      setIsFetchingPhoto(false)
      setIsImageLoading(false)
      return
    }

    let isMounted = true
    setHasError(false)
    setIsFetchingPhoto(true)
    setIsImageLoading(true)
    setPhoto('')

    api.get(`visit/photo/${photoId}`, { responseType: "blob" })
      .then(defaultSuccess)
      .then(({ data }) => {
        if (!isMounted) return

        if (!data?.size) {
          setHasError(true)
          setIsImageLoading(false)
          return
        }

        if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current)
        photoUrlRef.current = URL.createObjectURL(data)
        setPhoto(photoUrlRef.current)
      })
      .catch(error => {
        if (!isMounted) return
        setHasError(true)
        setIsImageLoading(false)
        defaultCatch(error, navigate)
      })
      .finally(() => {
        if (isMounted) setIsFetchingPhoto(false)
      })

    return () => {
      isMounted = false
    }
  }, [open, photoId, defaultSuccess, defaultCatch, navigate])

  useEffect(() => {
    return () => {
      if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current)
    }
  }, [])

  const showLoader = isFetchingPhoto || (Boolean(photo) && isImageLoading)

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogContent sx={{ p: '29px 30px' }}>
        <Stack gap={4}>
          <Stack direction="row" alignItems="center" gap={1}>
            <IconButton
              aria-label="close"
              onClick={onClose}
            >
              <ArrowBackIcon color='primary' />
            </IconButton>
            <label>
              {t("Identification photo")}
            </label>
          </Stack>
          <Box
            sx={{
              width: '100%',
              minHeight: '273px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            {photo && !hasError && (
              <Box
                component="img"
                src={photo}
                alt={t("Identification photo")}
                onLoad={() => setIsImageLoading(false)}
                onError={() => {
                  setHasError(true)
                  setIsImageLoading(false)
                }}
                sx={{
                  width: '100%',
                  maxHeight: '273px',
                  objectFit: 'contain',
                  borderRadius: '10px'
                }}
              />
            )}

            {showLoader && (
              <CircularProgress size={32} />
            )}

            {hasError && !showLoader && (
              <Typography variant="body2" color="text.secondary">
                {t('Could not load identification photo')}
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

const mapDispatchToProps = {
  defaultSuccess,
  defaultCatch
}

export default connect(null, mapDispatchToProps)(IDPhotoDialog)